/* Policy Studio registry — `PolicyManagerAgent`'s HTTP routes, reached through
   the Nitro catch-all proxy (`server/api/policy-builder/agents/[...].ts`),
   which injects the session's bearer token server-side. The worker then stamps
   the verified identity onto the forwarded request, so nothing here carries
   identity, and a create is attributed to the token rather than the body.

   CLIENT-SIDE ONLY: paths are relative, which native fetch cannot resolve
   during SSR, so every query built on this carries `enabled: import.meta.client`.

   Writes keep the 400 body (`ignoreResponseError`) because it carries the
   field-level rejection reasons; reads throw and the query surfaces them. */
import { POLICY_MANAGER_ROOM } from '@worker/policy-builder/client'
import type { BadgeProps } from '@nuxt/ui'
import type {
  CreatePolicyInput,
  MentionThreadRow,
  PolicyCounts,
  PolicyListFilters,
  PolicyPage,
  PolicyRole,
  PolicySummary,
  StudioMember,
} from '@worker/policy-builder/client'

const BASE = `/api/policy-builder/agents/policy-manager-agent/${POLICY_MANAGER_ROOM}`

export type CreatePolicyResult =
  | { id: string }
  | {
      ok: false
      status: number
      error?: string
      errors?: Record<string, string[]>
    }

/** One filtered page; `stage` is sent as repeated params, as the route reads it. */
const list = (filters: PolicyListFilters = {}) =>
  $fetch<PolicyPage>(`${BASE}/policies`, {
    method: 'GET',
    query: { ...filters },
  })

/* One client for the whole app; it reads no Nuxt context, so a module const. */
export const policyRegistry = {
  list,
  /**
   * Every row matching `filters`, paged through `nextCursor`.
   *
   * ponytail: stops after `maxPages` pages of 200 (2,000 policies), and every
   * queue still filters in the browser. Upgrade: drive search and filters into
   * the registry query and page on scroll.
   */
  listAll: async (
    filters: PolicyListFilters = {},
    maxPages = 10,
  ): Promise<PolicyPage> => {
    const first = await list({ limit: 200, ...filters })
    const rows = [...first.rows]
    let next = first.nextCursor
    for (let page = 1; next && page < maxPages; page++) {
      const more = await list({ limit: 200, ...filters, cursor: next })
      rows.push(...more.rows)
      next = more.nextCursor
    }
    return { rows, nextCursor: next, total: first.total }
  },
  /** One policy's row; rejects with a 404 when the id was never created. */
  get: (id: string) =>
    $fetch<PolicySummary>(`${BASE}/policies/${encodeURIComponent(id)}`, {
      method: 'GET',
    }),
  counts: () => $fetch<PolicyCounts>(`${BASE}/counts`, { method: 'GET' }),
  /** Open threads tagging any of `tags`, newest first; `tag` is sent as repeated params. */
  mentions: (tags: string[]) =>
    $fetch<{ rows: MentionThreadRow[] }>(`${BASE}/mentions`, {
      method: 'GET',
      query: { tag: tags },
    }),
  /** The signed-in person's teams and read marks. */
  me: () => $fetch<StudioMember>(`${BASE}/me`, { method: 'GET' }),
  setTeams: (teams: PolicyRole[]) =>
    $fetch(`${BASE}/me/teams`, { method: 'PUT', body: { teams } }),
  /** Moves read marks forward; the server keeps the later of two. */
  markSeen: (kind: 'thread' | 'question', seen: Record<string, number>) =>
    $fetch(`${BASE}/me/seen`, { method: 'POST', body: { kind, seen } }),
  /** Sets (or, with `null`, clears) one stage's owner without opening the policy. */
  assign: (id: string, role: PolicyRole, owner: string | null) =>
    $fetch(`${BASE}/policies/${encodeURIComponent(id)}/owner`, {
      method: 'PUT',
      body: { role, owner },
    }),
  /** Idempotent on `input.idempotencyKey`, or on `paapiPolicyId` when opening an existing policy. */
  create: async (input: CreatePolicyInput): Promise<CreatePolicyResult> => {
    const response = await $fetch.raw<CreatePolicyResult>(`${BASE}/policies`, {
      method: 'POST',
      body: input,
      ignoreResponseError: true,
    })
    return response.ok
      ? response._data!
      : { ...response._data, ok: false, status: response.status }
  },
}

/**
 * Makes sure Policy Studio has Content Library policy `id`: one it has never
 * opened is created from PaAPI, which names it. `false` when Content Library
 * has no such policy.
 *
 * @throws When the registry or PaAPI cannot be reached, or the caller may not open policies.
 */
export const ensureStudioPolicy = async (id: string): Promise<boolean> => {
  try {
    await policyRegistry.get(id)
    return true
  } catch (error) {
    if ((error as { statusCode?: number }).statusCode !== 404) throw error
  }
  const result = await policyRegistry.create({
    idempotencyKey: `paapi:${id}`,
    paapiPolicyId: id,
  })
  if ('id' in result) return true
  if (result.status === 404) return false
  throw new Error(result.error ?? `Could not open ${id} in Policy Studio`)
}

// --- classification-category badge color: a stable hash picks the color, so
// the same category always renders the same hue across views -----------------

const CATEGORY_PALETTE: BadgeProps['color'][] = [
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
  'neutral',
]

const categoryHash = (value: string): number => {
  let hash = 0
  for (let i = 0; i < value.length; i++)
    hash = Math.imul(hash, 31) + value.charCodeAt(i)
  return Math.abs(hash)
}

export const categoryColor = (category: string): BadgeProps['color'] =>
  CATEGORY_PALETTE[categoryHash(category) % CATEGORY_PALETTE.length]
